import { Stat } from './Stat'
import { usePortfolioSummary } from '../../hooks/usePortfolioSummary'
import { useMaskMoney, usePrivacyHidden, usePrivacyStore } from '../../privacy/usePrivacyStore'

function rawFmt(v: string | number | undefined | null): string {
  if (v === undefined || v === null || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function rawFmtSigned(n: number): string {
  const sign = n >= 0 ? '+' : '−'
  return `${sign}${Math.abs(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
}

export function BalanceRail() {
  const summary = usePortfolioSummary()
  const mask = useMaskMoney()
  const hidden = usePrivacyHidden()
  const togglePrivacy = usePrivacyStore.togglePrivacy
  const fmt = (v: string | number | undefined | null) => mask(rawFmt(v))
  const fmtSigned = (n: number) => mask(rawFmtSigned(n))
  const {
    data,
    isLoading,
    error,
    stockPositions,
    optionPositions,
    stocksValue,
    optionsValue,
    portfolioValue,
    dayChange,
    cashValue,
    cashPercentOfPortfolio,
    bp,
    cashBp,
    optionsBp,
    unsettled,
    pendingOrders,
    pendingReserve,
  } = summary

  return (
    <aside className="row-start-2 col-start-3 border-l border-zinc-800 bg-zinc-950 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-zinc-800 bg-white/[0.02] flex items-center justify-between">
        <div className="text-xs text-zinc-400 uppercase tracking-wider">Portfolio</div>
        <button
          type="button"
          onClick={togglePrivacy}
          aria-pressed={hidden}
          aria-label={hidden ? 'Show balances' : 'Hide balances'}
          title={hidden ? 'Show balances' : 'Hide balances'}
          className="text-zinc-400 hover:text-zinc-100 transition p-1 -m-1 rounded"
        >
          {hidden ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {isLoading && (
        <div className="px-4 py-3 text-zinc-500 text-sm">Loading balances…</div>
      )}
      {error ? (
        <div className="px-4 py-3 text-red-400 text-xs whitespace-pre-wrap break-all">
          {(error as Error).message}
        </div>
      ) : null}

      {data && (
        <div className="flex-1 overflow-y-auto">
          <Section title="Value">
            <Stat label="Total value" value={fmt(portfolioValue)} size="lg" />
            <Stat
              label="Day change"
              value={fmtSigned(dayChange)}
              accent={dayChange >= 0 ? 'emerald' : 'red'}
            />
            <Stat
              label="Long stocks"
              value={fmt(stocksValue)}
              hint={`${stockPositions.length} position${stockPositions.length === 1 ? '' : 's'}`}
            />
            <Stat
              label="Long options"
              value={fmt(optionsValue)}
              hint={`${optionPositions.length} contract${optionPositions.length === 1 ? '' : 's'}`}
            />
          </Section>

          <Section title="Buying power">
            <Stat label="Buying power" value={fmt(bp)} accent="emerald" hint="Available to trade" />
            <Stat label="Settled funds" value={fmt(cashBp)} hint="Cash · withdrawable" />
            <Stat label="Options BP" value={fmt(optionsBp)} hint="Usable for options" />
            <Stat
              label="Cash"
              value={fmt(cashValue)}
              hint={
                cashPercentOfPortfolio != null
                  ? `${cashPercentOfPortfolio.toFixed(1)}% of portfolio`
                  : undefined
              }
            />
          </Section>

          <Section title="Pending">
            <Stat
              label="Unsettled trades"
              value={fmt(unsettled)}
              hint="BP − settled"
              accent={unsettled > 0 ? 'amber' : undefined}
            />
            <Stat
              label="Pending orders"
              value={fmt(pendingReserve)}
              hint={`${pendingOrders.length} open`}
              accent={pendingOrders.length > 0 ? 'amber' : undefined}
            />
          </Section>

        </div>
      )}
    </aside>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="border-b border-zinc-800">
      <div className="px-4 py-2 text-xs text-zinc-400 uppercase tracking-wider bg-white/[0.02] border-b border-zinc-800">
        {title}
      </div>
      <div className="px-4 py-3 grid grid-cols-2 gap-x-3 gap-y-3">{children}</div>
    </div>
  )
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.43 10.43 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}
